import { Directive, OnDestroy } from '@angular/core'
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

import { LatLng } from '@shared/models/latlng.model'
import { FitBoundsOptions } from 'leaflet'

type HtmlElementName = 'link' | 'script'
type HtmlElementType = 'css' | 'js'

@Directive()
export abstract class AbstractMapAdapter implements OnDestroy {
  protected readonly onDestroy$ = new Subject<void>()

  private isScriptLoaded = new BehaviorSubject<boolean>(null)
  readonly isScriptLoaded$ = this.isScriptLoaded.asObservable().pipe(filter(Boolean), takeUntil(this.onDestroy$))

  private shouldCreateCustomControls = new BehaviorSubject<boolean>(null)
  readonly shouldCreateCustomControls$ = this.shouldCreateCustomControls.asObservable().pipe(filter(Boolean))

  setIsScriptLoaded(isLoaded: boolean) {
    this.isScriptLoaded.next(isLoaded)
  }

  setShouldCreateCustomControls(shouldCreateCustomControls: boolean) {
    this.shouldCreateCustomControls.next(shouldCreateCustomControls)
  }

  abstract initMap(map: HTMLElement): void
  abstract fitBounds(coordinates: LatLng | LatLng[], options?: FitBoundsOptions): void

  loadExternalResourse(url: string): Observable<HTMLScriptElement | HTMLLinkElement> {
    return new Observable((observer) => {
      let targetElement: HTMLScriptElement | HTMLLinkElement

      const type = this.getHtmlElementType(url)
      const htmlElementName = this.getHtmlElementName(type)
      const targetType = type === 'css' ? 'text/css' : 'text/javascript'

      // eslint-disable-next-line prefer-const
      targetElement = document.createElement(htmlElementName)
      targetElement.setAttribute('type', targetType)
      if (type === 'css') {
        targetElement.setAttribute('rel', 'stylesheet')
        targetElement.setAttribute('href', url)
        document.head.appendChild(targetElement)
      } else {
        targetElement.setAttribute('src', url)
        ;(targetElement as HTMLScriptElement).async = false
        document.body.appendChild(targetElement)
      }

      targetElement.onload = () => {
        observer.next(targetElement)
        observer.complete()
      }
    })
  }

  loadMultipleExternalResources(resourses: string[]) {
    if (document.querySelector(`script[src="${resourses[0]}"]`)) return of(null)

    const toObservable = (url: string) => this.loadExternalResourse(url)
    const observables = resourses.map(toObservable)
    return forkJoin(observables)
  }

  getHtmlElementType(url: string) {
    return url.match(/\.([^.]+)$/)[1] as HtmlElementType
  }

  getHtmlElementName(type: HtmlElementType): HtmlElementName {
    return type === 'css' ? 'link' : 'script'
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }
}
